import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, getToken } from "@/lib/api";
import { getUser, refreshMe } from "@/lib/userAuth";
import { MessageSquare, X, Send, Loader2, CalendarCheck, LogIn, Coins, Sparkles, Mic, MicOff, Paperclip } from "lucide-react";
import { readAttachment, ACCEPTED_ATTACHMENTS } from "@/lib/files";
import { AttachmentThumb } from "@/components/AttachmentThumb";

const SESSION_KEY = "labor_chat_session";

export default function Chatbot() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Salve! Sono Labor AI 🤖 — l'assistente virtuale del CAF Labor. Come posso aiutarti oggi? Puoi chiedermi info su 730, ISEE, pensioni, NASpI, orari, documenti…" },
  ]);
  const [input, setInput] = useState("");
  const [attachment, setAttachment] = useState(null); // {filename, base64}
  const [attaching, setAttaching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(getUser());
  const [recording, setRecording] = useState(false);
  const recognitionRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  // Sync automatico con lo stato di login: ascolta cambi di auth E tenta refresh se c'è token ma manca user
  useEffect(() => {
    const onAuthChange = (e) => setUser(e.detail || getUser());
    const onStorage = () => setUser(getUser());
    window.addEventListener("labor-auth-changed", onAuthChange);
    window.addEventListener("storage", onStorage);
    // Caso 1: ho già un token ma lo state è null → carico da /users/me
    if (!user && getToken()) {
      refreshMe().then((u) => u && setUser(u));
    }
    return () => {
      window.removeEventListener("labor-auth-changed", onAuthChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  useEffect(() => {
    // All'apertura della chat: se c'è token ma non user, provo refresh (token valido ma localStorage user svuotato)
    if (open) {
      if (!user && getToken()) {
        refreshMe().then((u) => u && setUser(u));
      } else if (open && user) {
        refreshMe().then((u) => u && setUser(u));
      }
    }
  }, [open]);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("open-labor-ai", handler);
    return () => window.removeEventListener("open-labor-ai", handler);
  }, []);

  const nowTime = () => new Date().toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });

  const toggleRecording = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      alert("Il tuo browser non supporta il riconoscimento vocale. Usa Chrome o Safari su mobile.");
      return;
    }
    if (recording) {
      recognitionRef.current?.stop();
      setRecording(false);
      return;
    }
    const rec = new SR();
    rec.lang = "it-IT";
    rec.interimResults = true;
    rec.continuous = false;
    rec.onresult = (e) => {
      let transcript = "";
      for (let i = 0; i < e.results.length; i++) transcript += e.results[i][0].transcript;
      setInput(transcript);
    };
    rec.onend = () => setRecording(false);
    rec.onerror = () => setRecording(false);
    recognitionRef.current = rec;
    rec.start();
    setRecording(true);
  };

  const getSession = () => {
    let s = localStorage.getItem(SESSION_KEY);
    if (!s) { s = "sess_" + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem(SESSION_KEY, s); }
    return s;
  };

  const handleAttach = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setAttaching(true);
    try { setAttachment(await readAttachment(f)); }
    catch (err) { alert(err.message || "Errore lettura file"); }
    finally { setAttaching(false); }
  };

  const send = async () => {
    const text = input.trim();
    if ((!text && !attachment) || loading) return;
    // Require auth to prevent abuse and fake accounts
    if (!user) {
      setMessages((m) => [...m, { role: "user", text: text || "(allegato)", ts: nowTime() }, { role: "assistant", text: "Per usare Labor AI e ricevere risposte affidabili devi prima registrarti (basta email, nome, cognome, telefono e password) o accedere. La registrazione è gratuita e ti dà 20 domande gratuite.", action: "signup_required", ts: nowTime() }]);
      setInput(""); setAttachment(null);
      return;
    }
    const userMsg = attachment ? (text || (attachment.previewUrl ? "Analizza questa foto" : "Analizza questo documento")) : text;
    const att = attachment ? { filename: attachment.filename, mime: attachment.mime, previewUrl: attachment.previewUrl } : null;
    setMessages((m) => [...m, { role: "user", text: userMsg, attachment: att, ts: nowTime() }]);
    const payload = { session_id: getSession(), message: text };
    if (attachment) {
      payload.attachment_filename = attachment.filename;
      payload.attachment_base64 = attachment.base64;
    }
    setInput(""); setAttachment(null);
    setLoading(true);
    try {
      const res = await api.post("/chat", payload);
      setMessages((m) => [...m, { role: "assistant", text: res.data.reply, action: res.data.action, cost: res.data.cost, used_type: res.data.used_type, ts: nowTime() }]);
      if (user) {
        const updates = {};
        if (typeof res.data.labor_points === "number") updates.labor_points = res.data.labor_points;
        if (typeof res.data.free_questions === "number") updates.free_questions = res.data.free_questions;
        if (Object.keys(updates).length) setUser({ ...user, ...updates });
      }
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: "Al momento non riesco a rispondere. Puoi prenotare un appuntamento o chiamare il 379 146 6566.", ts: nowTime() }]);
    } finally { setLoading(false); }
  };

  return (
    <>
      {open && (
        <div className="fixed inset-0 sm:inset-auto sm:bottom-5 sm:right-5 sm:w-[420px] sm:h-[85vh] z-50 bg-white flex flex-col animate-fade-in shadow-2xl sm:rounded-md overflow-hidden">
          <div className="flex items-center justify-between p-3 border-b border-labor-olive/20 bg-labor-olive text-white">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="font-display text-base leading-tight">Labor AI</div>
                <div className="text-[11px] opacity-85 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span> online · il tuo assistente CAF
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {user && (
                <div className="text-right text-[11px] px-2 py-0.5 bg-white/10 rounded-full">
                  <span className="opacity-80 mr-1">{(user.free_questions ?? 0) > 0 ? "🎁" : "💎"}</span>
                  <span className="font-bold" data-testid="chat-points">
                    {(user.free_questions ?? 0) > 0 ? user.free_questions : (user.labor_points ?? 0)}
                  </span>
                </div>
              )}
              <button data-testid="chatbot-close" onClick={() => setOpen(false)} aria-label="Chiudi" className="p-1.5 hover:bg-white/10 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {!user && (
            <div className="p-3 bg-labor-warning/10 border-b border-labor-border text-[13px] flex items-center gap-2">
              <span>🎁 <strong>Registrati gratis</strong> per 20 domande gratuite a Labor AI</span>
              <button onClick={() => { setOpen(false); nav("/accedi"); }} data-testid="chat-signup" className="ml-auto olive-btn px-3 py-1 text-xs font-semibold flex items-center gap-1"><LogIn className="w-3.5 h-3.5" /> Accedi</button>
            </div>
          )}

          <div
            data-testid="chatbot-messages"
            className="flex-1 overflow-y-auto p-4 space-y-2"
            style={{
              background: "#ECE5DD",
              backgroundImage: "radial-gradient(rgba(0,0,0,0.03) 1px, transparent 1px)",
              backgroundSize: "22px 22px",
            }}
          >
            {messages.map((m, i) => {
              const t = m.ts || "";
              return (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-3 py-2 text-[14.5px] leading-snug shadow-sm relative ${
                  m.role === "user"
                    ? "bg-[#DCF8C6] text-labor-ink rounded-2xl rounded-br-sm"
                    : "bg-white text-labor-ink rounded-2xl rounded-bl-sm"
                }`}>
                  {m.attachment && (
                    <div className="mb-1.5">
                      <AttachmentThumb mime={m.attachment.mime} filename={m.attachment.filename} previewUrl={m.attachment.previewUrl} testId={`chat-msg-thumb-${i}`} />
                    </div>
                  )}
                  <div className="whitespace-pre-wrap break-words">{m.text}</div>
                  <div className="text-[10px] mt-1 opacity-55 text-right flex items-center justify-end gap-1">
                    {m.cost != null && m.role === "assistant" && (
                      <span className="mr-1">{m.used_type === "free" ? "gratis" : `-${Math.abs(m.cost)} LP`}</span>
                    )}
                    <span>{t}</span>
                  </div>
                  {m.action === "suggest_booking" && (
                    <button
                      data-testid={`chat-book-cta-${i}`}
                      onClick={() => { setOpen(false); nav("/prenota"); }}
                      className="mt-3 w-full olive-btn px-3 py-2 text-sm font-semibold flex items-center justify-center gap-2"
                    >
                      <CalendarCheck className="w-4 h-4" /> Prenota un appuntamento
                    </button>
                  )}
                  {m.action === "signup_required" && (
                    <button
                      data-testid={`chat-signup-cta-${i}`}
                      onClick={() => { setOpen(false); nav("/registrati"); }}
                      className="mt-3 w-full olive-btn px-3 py-2 text-sm font-semibold flex items-center justify-center gap-2"
                    >
                      <LogIn className="w-4 h-4" /> Registrati gratis
                    </button>
                  )}
                  {m.action === "recharge_needed" && (
                    <button
                      onClick={() => { setOpen(false); nav("/profilo"); }}
                      className="mt-3 w-full terracotta-btn px-3 py-2 text-sm font-semibold flex items-center justify-center gap-2"
                    >
                      <Coins className="w-4 h-4" /> Ricarica Labor Point
                    </button>
                  )}
                </div>
              </div>
            );})}
            {loading && <div className="flex items-center gap-2 text-labor-muted text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Labor AI sta pensando…</div>}
            <div ref={endRef} />
          </div>
          <div className="p-3 border-t border-labor-border bg-white">
            {attachment && (
              <div className="flex items-center gap-3 bg-labor-skyBg border border-labor-border p-2 mb-2 text-xs" data-testid="chat-attachment-preview">
                {attachment.previewUrl
                  ? <img src={attachment.previewUrl} alt={attachment.filename} className="w-14 h-14 object-cover rounded-md border border-labor-border" data-testid="chat-attachment-thumb" />
                  : <div className="w-11 h-14 bg-red-600 text-white text-[10px] font-bold flex items-center justify-center rounded-sm" data-testid="chat-attachment-thumb">PDF</div>}
                <span className="flex-1 truncate font-medium">{attachment.filename}</span>
                <span className="text-labor-muted">· costerà 8 LP</span>
                <button
                  data-testid="chat-attachment-remove"
                  onClick={() => setAttachment(null)}
                  className="text-red-600 hover:bg-red-50 p-0.5"
                  aria-label="Rimuovi allegato"
                ><X className="w-3.5 h-3.5" /></button>
              </div>
            )}
            <div className="flex gap-2">
              <input data-testid="chatbot-input" value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder={recording ? "🔴 Sto ascoltando…" : (attachment ? "Cosa vuoi sapere su questo file?" : "Scrivi o parla la tua domanda…")}
                className="flex-1 px-3 py-2.5 border border-labor-border focus:outline-none focus:ring-2 focus:ring-labor-terracotta text-[15px]" />
              <label
                data-testid="chatbot-attach-label"
                title="Allega un PDF o una foto per farlo analizzare dall'IA (costa 8 LP)"
                className={`px-3.5 py-2.5 border cursor-pointer flex items-center ${attaching ? "opacity-50" : "border-labor-olive text-labor-olive hover:bg-labor-olive hover:text-white"}`}
              >
                {attaching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                <input data-testid="chatbot-attach-input" type="file" accept={ACCEPTED_ATTACHMENTS} onChange={handleAttach} className="hidden" />
              </label>
              <button
                data-testid="chatbot-mic"
                onClick={toggleRecording}
                className={`px-3.5 py-2.5 border ${recording ? "bg-red-600 text-white border-red-600 animate-pulse" : "border-labor-olive text-labor-olive hover:bg-labor-olive hover:text-white"}`}
                title="Registra un messaggio vocale"
              >
                {recording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>
              <button data-testid="chatbot-send" onClick={send} disabled={loading} className="olive-btn px-3.5 py-2.5">
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
